import { useMemo, useState, createContext, useContext } from "react";
import {
    type ColumnDef,
    type SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/shadcn/table";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/shadcn/tooltip";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { ScoutingGoalkeeper, ScoutingKpiInfo } from "@/api/scouting";
import { Loader } from "../common/loader";
import { getPointsBadge, getTeamLogo } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { FollowPlayerButton } from "./FollowPlayerButton";

interface ScoutingGoalkeepersTableProps {
    data: ScoutingGoalkeeper[];
    isLoading?: boolean;
    selectedCategory: StatCategory;
    referenceData?: ScoutingKpiInfo;
    onTeamClick?: (team: string) => void;
    activeTeamFilters?: string[];
    followedPlayerIds?: number[];
}

const MobileCompactContext = createContext(false);

const KpiBadge = ({ value, refValue }: { value: number; refValue?: number }) => {
    const isCompact = useContext(MobileCompactContext);
    const badgeColors = value > 0 ? getPointsBadge(value) : null;
    const delta = !isCompact && value > 0 && refValue != null && refValue > 0 ? value - refValue : null;
    return (
        <div className="flex items-center gap-1.5">
            {badgeColors ? (
                <div
                    className={`inline-flex items-center justify-center w-12 py-0.5 ${badgeColors.bg} ${badgeColors.text} rounded font-bold text-xs border ${badgeColors.border}`}
                >
                    {value.toFixed(1)}
                </div>
            ) : (
                <div className="inline-flex items-center justify-center w-12 py-0.5 text-on-surface-variant text-xs">
                    -
                </div>
            )}
            {delta !== null && (
                <span
                    className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${delta > 0 ? "text-green-600" : delta < 0 ? "text-red-500" : "text-on-surface-variant"}`}
                >
                    {delta > 0 ? (
                        <ArrowUp className="h-2.5 w-2.5" />
                    ) : delta < 0 ? (
                        <ArrowDown className="h-2.5 w-2.5" />
                    ) : null}
                    {delta > 0 ? "+" : delta === 0 ? "=" : ""}
                    {delta !== 0 ? delta.toFixed(1) : "0.0"}
                </span>
            )}
        </div>
    );
};

export type StatCategory = "overview" | "performance" | "passing" | "screens";

const LOWER_IS_BETTER_COLUMNS = [
    "performanceStats.goalsAgainst",
    "performanceStats.goalsAgainstAverage",
    "withScreenStats.goalsAgainstWithScreen",
];

const formatValue = (value: number): string => {
    if (value === 0) return "-";
    return new Intl.NumberFormat("en-US").format(value);
};

export function ScoutingGoalkeepersTable({
    data,
    isLoading = false,
    selectedCategory,
    referenceData,
    onTeamClick,
    activeTeamFilters = [],
    followedPlayerIds,
}: ScoutingGoalkeepersTableProps) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const navigate = useNavigate();

    const columns = useMemo((): ColumnDef<ScoutingGoalkeeper>[] => {
        const baseColumns: ColumnDef<ScoutingGoalkeeper>[] = [
            {
                accessorKey: "goalKeeperIdentity.goalKeeperName",
                header: "Goalkeeper",
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <FollowPlayerButton
                            playerId={row.original.goalKeeperIdentity.goalKeeperId}
                            isFollowed={
                                followedPlayerIds?.includes(row.original.goalKeeperIdentity.goalKeeperId) ?? false
                            }
                        />
                        <div
                            className="font-semibold cursor-pointer hover:underline"
                            style={{ color: "#050066" }}
                            onClick={() =>
                                navigate({
                                    to: `/player/${row.original.goalKeeperIdentity.goalKeeperId}`,
                                    search: { teamId: row.original.goalKeeperIdentity.teamId },
                                })
                            }
                        >
                            {row.original.goalKeeperIdentity.goalKeeperName}
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: "goalKeeperIdentity.teamName",
                header: "Team",
                cell: ({ row }) => {
                    const team = row.original.goalKeeperIdentity.teamName;
                    const isActive = activeTeamFilters.includes(team);
                    if (!onTeamClick) return team;
                    return (
                        <span
                            onClick={() => onTeamClick(team)}
                            className={`cursor-pointer rounded px-1 -mx-1 transition-colors hover:bg-primary/10 ${isActive ? "text-primary font-semibold" : ""}`}
                        >
                            {team}
                        </span>
                    );
                },
            },
            {
                accessorKey: "goalKeeperIdentity.role",
                header: "Role",
                cell: ({ row }) => row.original.goalKeeperIdentity.role,
            },
        ];

        const categoryColumns: Record<StatCategory, ColumnDef<ScoutingGoalkeeper>[]> = {
            overview: [
                {
                    accessorKey: "goalKeeperIdentity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.goalKeeperIdentity.matchesPlayed),
                },
                {
                    id: "kpiAvg",
                    header: "KPI Avg",
                    accessorFn: (row) => row.kpiInfo.overall?.avgKpi ?? 0,
                    cell: ({ row }) => {
                        const val = row.original.kpiInfo.overall?.avgKpi ?? 0;
                        const refVal = referenceData?.byPosition.find((p) => p.position === "Overall")?.avgKpi;
                        return <KpiBadge value={val} refValue={refVal} />;
                    },
                },
                {
                    id: "valarengaAvgKpi",
                    header: "Vålerenga Avg",
                    accessorFn: () => referenceData?.byPosition.find((p) => p.position === "Overall")?.avgKpi ?? 0,
                    cell: () => {
                        const val = referenceData?.byPosition.find((p) => p.position === "Overall")?.avgKpi ?? 0;
                        return <KpiBadge value={val} />;
                    },
                },
                {
                    accessorKey: "performanceStats.saves",
                    header: "Saves",
                    cell: ({ row }) => formatValue(row.original.performanceStats.saves),
                },
                {
                    accessorKey: "performanceStats.goalsAgainst",
                    header: "Goals Against",
                    cell: ({ row }) => formatValue(row.original.performanceStats.goalsAgainst),
                },
                {
                    accessorKey: "performanceStats.savePercentage",
                    header: "Save %",
                    cell: ({ row }) => `${(row.original.performanceStats.savePercentage * 100).toFixed(1)}%`,
                },
                {
                    accessorKey: "performanceStats.timeOnIce",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">TOI</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Time on Ice (seconds)</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => {
                        const seconds = row.original.performanceStats.timeOnIce;
                        const minutes = Math.floor(seconds / 60);
                        const secs = Math.floor(seconds % 60);
                        return `${minutes}:${secs.toString().padStart(2, "0")}`;
                    },
                },
            ],
            performance: [
                {
                    accessorKey: "goalKeeperIdentity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.goalKeeperIdentity.matchesPlayed),
                },
                {
                    accessorKey: "performanceStats.timeOnIce",
                    header: "Time on Ice",
                    cell: ({ row }) => {
                        const seconds = row.original.performanceStats.timeOnIce;
                        const minutes = Math.floor(seconds / 60);
                        return `${minutes} min`;
                    },
                },
                {
                    accessorKey: "performanceStats.saves",
                    header: "Saves",
                    cell: ({ row }) => formatValue(row.original.performanceStats.saves),
                },
                {
                    accessorKey: "performanceStats.goalsAgainst",
                    header: "Goals Against",
                    cell: ({ row }) => formatValue(row.original.performanceStats.goalsAgainst),
                },
                {
                    accessorKey: "performanceStats.goalsAgainstAverage",
                    header: "GAA",
                    cell: ({ row }) => {
                        const gaa = row.original.performanceStats.goalsAgainstAverage;
                        return gaa === null || gaa === undefined || gaa === 0 ? "-" : gaa;
                    },
                },
                {
                    accessorKey: "performanceStats.savePercentage",
                    header: "Save Percentage",
                    cell: ({ row }) => `${(row.original.performanceStats.savePercentage * 100).toFixed(1)}%`,
                },
            ],
            passing: [
                {
                    accessorKey: "goalKeeperIdentity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.goalKeeperIdentity.matchesPlayed),
                },
                {
                    accessorKey: "passingStats.successfulPasses",
                    header: "Successful Passes",
                    cell: ({ row }) => formatValue(row.original.passingStats.successfulPasses),
                },
                {
                    accessorKey: "passingStats.allPasses",
                    header: "Total Passes",
                    cell: ({ row }) => formatValue(row.original.passingStats.allPasses),
                },
                {
                    id: "passAccuracy",
                    header: "Pass Accuracy %",
                    accessorFn: (row) =>
                        row.passingStats.allPasses > 0
                            ? ((row.passingStats.successfulPasses / row.passingStats.allPasses) * 100).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
            ],
            screens: [
                {
                    accessorKey: "goalKeeperIdentity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.goalKeeperIdentity.matchesPlayed),
                },
                {
                    accessorKey: "withScreenStats.shotsAgainstWithScreen",
                    header: "Shots Against w/ Screen",
                    cell: ({ row }) => formatValue(row.original.withScreenStats.shotsAgainstWithScreen),
                },
                {
                    accessorKey: "withScreenStats.goalsAgainstWithScreen",
                    header: "Goals Against w/ Screen",
                    cell: ({ row }) => formatValue(row.original.withScreenStats.goalsAgainstWithScreen),
                },
                {
                    id: "savePercentageWithScreen",
                    header: "Save % w/ Screen",
                    accessorFn: (row) => {
                        const saves =
                            row.withScreenStats.shotsAgainstWithScreen - row.withScreenStats.goalsAgainstWithScreen;
                        const shots = row.withScreenStats.shotsAgainstWithScreen;
                        return shots > 0 ? ((saves / shots) * 100).toFixed(1) : "0.0";
                    },
                    cell: ({ getValue }) => `${getValue()}%`,
                },
            ],
        };

        return [...baseColumns, ...categoryColumns[selectedCategory]];
    }, [selectedCategory, referenceData, onTeamClick, activeTeamFilters, followedPlayerIds]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: { sorting },
    });

    const getSortDirection = (header: {
        column: { getCanSort: () => boolean; getIsSorted: () => false | "asc" | "desc" };
    }) => {
        if (!header.column.getCanSort()) return null;
        return header.column.getIsSorted();
    };

    const columnRankings = useMemo(() => {
        const rankings: Record<
            string,
            {
                highest: number | null;
                second: number | null;
                third: number | null;
                isLowerBetter: boolean;
            }
        > = {};

        if (!data || data.length === 0) {
            return rankings;
        }

        const currentColumns = columns.slice(3);

        currentColumns.forEach((columnDef) => {
            const colDef = columnDef as {
                id?: string;
                accessorKey?: string;
                accessorFn?: (row: ScoutingGoalkeeper, index: number) => unknown;
            };

            const columnId = (colDef.accessorKey as string) || colDef.id || "";
            if (!columnId) return;

            const isLowerBetter = LOWER_IS_BETTER_COLUMNS.includes(columnId);

            const columnValues = data
                .map((row, index) => {
                    let value: unknown;

                    if (colDef.accessorFn) {
                        value = colDef.accessorFn(row, index);
                    } else if (colDef.accessorKey) {
                        const keys = (colDef.accessorKey as string).split(".");
                        let result: unknown = row;
                        for (const key of keys) {
                            if (result && typeof result === "object") {
                                result = (result as Record<string, unknown>)[key];
                            } else {
                                result = null;
                                break;
                            }
                        }
                        value = result;
                    }

                    if (typeof value === "string") {
                        const cleaned = value.replace(/[^0-9.-]/g, "");
                        const parsed = parseFloat(cleaned);
                        return isNaN(parsed) ? null : parsed;
                    }

                    if (typeof value === "number") {
                        return value;
                    }

                    return null;
                })
                .filter((value): value is number => value !== null && !isNaN(value) && value !== 0);

            if (columnValues.length === 0) {
                return;
            }

            const sortedValues = [...columnValues].sort((a, b) => (isLowerBetter ? a - b : b - a));
            const uniqueValues = [...new Set(sortedValues)];

            rankings[columnId] = {
                highest: uniqueValues.length > 0 ? uniqueValues[0] : null,
                second: uniqueValues.length > 1 ? uniqueValues[1] : null,
                third: uniqueValues.length > 2 ? uniqueValues[2] : null,
                isLowerBetter,
            };
        });

        return rankings;
    }, [data, columns]);

    const mobileHeaders = table.getHeaderGroups()[0]?.headers ?? [];

    if (isLoading) {
        return (
            <div className="animate-slide-up w-full min-h-0 h-[200px] flex items-center justify-center bg-surface-container-lowest">
                <Loader message="Loading goalkeepers ranking..." />
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className="animate-slide-up w-full min-h-0 h-[200px] flex items-center justify-center bg-surface-container-lowest">
                <span className="text-center p-4 text-on-surface-variant">No goalkeepers ranking data available</span>
            </div>
        );
    }

    return (
        <>
            <div className="hidden lg:block w-full overflow-auto h-full min-h-0 bg-surface-container-lowest">
                <Table className="w-full min-w-max">
                    <TableHeader>
                        {table.getHeaderGroups().map((hg) => (
                            <TableRow key={hg.id}>
                                {hg.headers.map((header, idx) => {
                                    const sortDir = getSortDirection(header);
                                    return (
                                        <TableHead
                                            key={header.id}
                                            onClick={
                                                header.column.getCanSort()
                                                    ? () =>
                                                          header.column.toggleSorting(
                                                              header.column.getIsSorted() === "asc"
                                                          )
                                                    : undefined
                                            }
                                            className={`
                                                px-6 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest
                                                sticky z-20 select-none transition-colors whitespace-nowrap
                                                ${header.column.getCanSort() ? "cursor-pointer" : ""}
                                                ${idx < 3 ? "z-30" : ""}
                                                ${sortDir ? "data-table-header-sorted" : ""}
                                                data-table-header
                                            `}
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                left:
                                                    idx === 0
                                                        ? "0px"
                                                        : idx === 1
                                                          ? "200px"
                                                          : idx === 2
                                                            ? "350px"
                                                            : undefined,
                                                minWidth:
                                                    idx === 0
                                                        ? "200px"
                                                        : idx === 1
                                                          ? "150px"
                                                          : idx === 2
                                                            ? "150px"
                                                            : undefined,
                                                width:
                                                    idx === 0
                                                        ? "200px"
                                                        : idx === 1
                                                          ? "150px"
                                                          : idx === 2
                                                            ? "150px"
                                                            : undefined,
                                            }}
                                        >
                                            {!header.isPlaceholder && (
                                                <div className="flex gap-1.5 items-center">
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {header.column.getCanSort() && (
                                                        <span>
                                                            {sortDir === "asc" ? (
                                                                <ArrowUp
                                                                    className="h-3.5 w-3.5"
                                                                    style={{ color: "var(--color-red)" }}
                                                                />
                                                            ) : sortDir === "desc" ? (
                                                                <ArrowDown
                                                                    className="h-3.5 w-3.5"
                                                                    style={{ color: "var(--color-green)" }}
                                                                />
                                                            ) : (
                                                                <ArrowUpDown className="h-3.5 w-3.5 text-on-surface-variant/30" />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <TableRow key={row.id} className="data-table-row transition-colors">
                                    {row.getVisibleCells().map((cell, idx) => {
                                        if (idx < 3) {
                                            return (
                                                <TableCell
                                                    key={cell.id}
                                                    className="data-table-label-cell sticky z-10 px-6 py-3.5 text-sm whitespace-nowrap"
                                                    style={{
                                                        fontWeight: idx === 0 ? 700 : 400,
                                                        color: idx === 0 ? "#050066" : "inherit",
                                                        left: idx === 0 ? "0px" : idx === 1 ? "200px" : "350px",
                                                        minWidth: idx === 0 ? "200px" : idx === 1 ? "150px" : "150px",
                                                        width: idx === 0 ? "200px" : idx === 1 ? "150px" : "150px",
                                                    }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            );
                                        }

                                        const value = cell.getValue();
                                        const columnDef = cell.column.columnDef as {
                                            accessorKey?: string;
                                            id?: string;
                                        };
                                        const columnId = (columnDef.accessorKey as string) || cell.column.id;

                                        let valueColor: string | undefined = undefined;

                                        let numericValue: number | null = null;
                                        if (typeof value === "number") {
                                            numericValue = value;
                                        } else if (typeof value === "string") {
                                            const cleaned = value.replace(/[^0-9.-]/g, "");
                                            const parsed = parseFloat(cleaned);
                                            numericValue = isNaN(parsed) ? null : parsed;
                                        }

                                        if (numericValue !== null && numericValue !== 0 && columnRankings[columnId]) {
                                            const { highest, second, third } = columnRankings[columnId];

                                            const epsilon = 0.001;
                                            const matches = (a: number, b: number | null) =>
                                                b !== null && Math.abs(a - b) < epsilon;

                                            if (matches(numericValue, highest)) {
                                                valueColor = "var(--color-medal-gold)";
                                            } else if (matches(numericValue, second)) {
                                                valueColor = "var(--color-rank-silver)";
                                            } else if (matches(numericValue, third)) {
                                                valueColor = "var(--color-rank-bronze)";
                                            }
                                        }

                                        return (
                                            <TableCell
                                                key={cell.id}
                                                className="px-6 py-3.5 text-sm text-on-surface whitespace-nowrap"
                                                style={{
                                                    color: valueColor,
                                                    fontWeight: valueColor ? 700 : undefined,
                                                }}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            <div className="lg:hidden w-full">
                <div className="flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden">
                    {table.getRowModel().rows.map((row, index) => {
                        const cells = row.getVisibleCells();
                        const rank = index + 1;
                        const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                        const teamName = row.original.goalKeeperIdentity.teamName;
                        const teamLogo = getTeamLogo(teamName);

                        return (
                            <div key={row.id} className={`flex items-stretch py-3 ${rowBg}`}>
                                <div className="flex flex-col items-center w-9 shrink-0 gap-2 px-1">
                                    <FollowPlayerButton
                                        playerId={row.original.goalKeeperIdentity.goalKeeperId}
                                        isFollowed={
                                            followedPlayerIds?.includes(row.original.goalKeeperIdentity.goalKeeperId) ??
                                            false
                                        }
                                    />
                                    <div className="flex-1 flex items-center justify-center">
                                        <span
                                            className={`font-headline font-bold text-sm tabular-nums ${
                                                rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                                            }`}
                                        >
                                            #{rank.toString().padStart(2, "0")}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0 pr-3">
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <div
                                            className="font-semibold text-sm cursor-pointer hover:underline truncate flex-1 min-w-0"
                                            style={{ color: "#050066" }}
                                            onClick={() =>
                                                navigate({
                                                    to: `/player/${row.original.goalKeeperIdentity.goalKeeperId}`,
                                                    search: { teamId: row.original.goalKeeperIdentity.teamId },
                                                })
                                            }
                                        >
                                            {row.original.goalKeeperIdentity.goalKeeperName}
                                        </div>
                                        {teamLogo ? (
                                            <img
                                                src={teamLogo}
                                                alt={teamName}
                                                className="h-5 w-5 object-contain shrink-0"
                                                title={teamName}
                                            />
                                        ) : (
                                            <span className="text-xs text-on-surface-variant shrink-0 max-w-12 truncate">
                                                {teamName}
                                            </span>
                                        )}
                                        <span className="px-1.5 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant shrink-0">
                                            {row.original.goalKeeperIdentity.role}
                                        </span>
                                    </div>
                                    <div
                                        className={`grid gap-1.5 ${cells.slice(3).length > 6 ? "grid-cols-4" : "grid-cols-3"}`}
                                    >
                                        {cells.slice(3).map((cell, i) => {
                                            const header = mobileHeaders[i + 3];
                                            return (
                                                <div
                                                    key={cell.id}
                                                    className="flex flex-col items-center gap-0.5 min-w-0 rounded-md border border-outline-variant/30 px-1 py-1.5"
                                                >
                                                    <span className="text-[9px] uppercase font-bold text-on-surface-variant tracking-wider text-center truncate w-full">
                                                        {header &&
                                                            flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                    </span>
                                                    <MobileCompactContext.Provider value={true}>
                                                        <div className="text-sm font-bold tabular-nums text-on-surface leading-none text-center">
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </div>
                                                    </MobileCompactContext.Provider>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}
